import React, { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  User,
  Users,
  ChevronDown,
  Package,
  ShoppingCart,
  BarChart3,
  FileText,
  Settings,
  Heart,
  HelpCircle,
  Store,
  LayoutDashboard,
  LogOut,
  CreditCard,
  Mail,
  MessageCircle,
} from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
} from "@b2b/ui-components";
import { navigationConfigs, NavigationItem } from "@b2b/ui-components";
import { BizcoverIndiaBrandMark } from "../Branding/BizcoverIndiaBrandMark";
import {
  HeaderChatMessagesSlot,
  type HeaderChatUnread,
} from "./HeaderChatMessages";
import {
  HeaderNotificationsSlot,
  type HeaderNotifications,
} from "./HeaderNotifications";
import { resolveHeaderAvatarUrl } from "./buyerHeaderAvatar";
import { FiMenu } from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
// Centralized icon map
const iconMap = {
  Users,
  Package,
  ShoppingCart,
  BarChart3,
  FileText,
  Settings,
  Heart,
  HelpCircle,
  Store,
  LayoutDashboard,
  CreditCard,
  User,
  MessageCircle,
  Mail,
};

interface HeaderProps {
  role: "admin" | "buyer" | "seller" | "both";
  userRole?: "admin" | "seller" | "buyer";
  onToggleSidebar?: () => void;
  isSidebarOpen?: boolean;
  onLogout?: () => void; // Optional logout handler from AuthProvider
  /** When provided (seller), profile menu is the same on every screen, driven by sidebar logic. */
  sellerUserMenuItems?: NavigationItem[];
  /** Optional center content below nav on small screens, inline on large (e.g. Sellers search). */
  headerCenter?: ReactNode;
  searchBar?: ReactNode;
  /** Live chat unread badge + dropdown; from webapp `useChatUnreadHeader()` when available. */
  chatUnread?: HeaderChatUnread;
  notifications?: HeaderNotifications;
  /** @deprecated Prefer `notifications.unreadCount` */
  notificationUnreadCount?: number;
}

const Header: React.FC<HeaderProps> = ({
  role,
  userRole: userRoleProp,
  onToggleSidebar,
  isSidebarOpen,
  onLogout,
  sellerUserMenuItems,
  headerCenter,
  searchBar,
  chatUnread,
  notifications,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = navigationConfigs[role];
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [emailPasswordUser, setEmailPasswordUser] = useState<{
    name?: string;
    email?: string;
    role?: string;
  } | null>(null);

  const userRoleFromAuth = undefined; // We'll rely on props or API fetch for role
  const [actualUserRole, setActualUserRole] = useState<
    "admin" | "seller" | "buyer" | undefined
  >(userRoleProp || userRoleFromAuth);

  const [buyerAvatarUrl, setBuyerAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    const checkToken = () => {
      setHasToken(!!localStorage.getItem("bt_b2b_access_token"));
    };
    checkToken();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "bt_b2b_access_token") checkToken();
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (!hasToken) {
      setBuyerAvatarUrl(null);
      return;
    }

    let cancelled = false;

    const fetchUserData = async (previewProfileImageUrl?: string) => {
      try {
        const { authService } = await import("@b2b/services");
        const meResponse = await authService.getMe();
        if (cancelled) return;

        if (meResponse.status === "active" && meResponse.user) {
          const user = meResponse.user;
          const userName =
            user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.firstName || user.email || "";

          setEmailPasswordUser({
            name: userName,
            email: user.email,
            role: user.role,
          });

          // Also update actualUserRole if not set - use functional update to avoid dependency
          setActualUserRole((currentRole) => {
            if (!currentRole && user.role) {
              const roleFromApi = user.role.toLowerCase();
              if (
                roleFromApi === "super_admin" ||
                roleFromApi === "company_admin"
              ) {
                return "admin";
              } else if (
                roleFromApi === "seller" ||
                roleFromApi === "buyer_seller"
              ) {
                return "seller";
              } else if (roleFromApi === "buyer") {
                return "buyer";
              }
            }
            return currentRole;
          });

          const resolvedAvatarUrl = await resolveHeaderAvatarUrl(
            role,
            user,
            previewProfileImageUrl,
          );
          if (!cancelled) setBuyerAvatarUrl(resolvedAvatarUrl);
        } else {
          setBuyerAvatarUrl(null);
        }
      } catch (error) {
        console.error("Error fetching user data in Header:", error);
        if (!cancelled) setBuyerAvatarUrl(null);
      }
    };

    void fetchUserData();

    const onProfileUpdated = (e: Event) => {
      const detail = (e as CustomEvent<{ profileImageUrl?: string }>).detail;
      void fetchUserData(detail?.profileImageUrl);
    };
    window.addEventListener("bt_b2b_profile_updated", onProfileUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener("bt_b2b_profile_updated", onProfileUpdated);
    };
  }, [hasToken, role]);

  const isAuthenticated = hasToken;

  const pathname = location.pathname;

  // Buyer/seller app pages that use the same simplified header chrome as dashboard
  const isDashboardPage =
    pathname === "/admin/dashboard" ||
    pathname === "/buyer/dashboard" ||
    pathname === "/buyer/analytics" ||
    pathname === "/buyer/favorites" ||
    pathname === "/buyer/enquiries" ||
    pathname === "/buyer/profile_settings" ||
    pathname.startsWith("/buyer/profile_settings/") ||
    pathname === "/seller/dashboard" ||
    pathname === "/seller/analytics";

  // Check if we're on pages where Categories, All products, Sellers, and Pricing should be hidden
  const isPricingPage =
    pathname === "/pricing" ||
    pathname === "/membership-plans" ||
    pathname.startsWith("/pricing/");
  const isProductSearchPage =
    pathname === "/search" || pathname.startsWith("/search/");
  const isProductDetailPage =
    pathname.startsWith("/product/") ||
    pathname.startsWith("/bizcover/product/");
  const isChatPage = pathname === "/chat" || pathname.startsWith("/chat");
  const shouldHideNavButtons =
    isPricingPage || isProductSearchPage || isProductDetailPage || isChatPage;

  // Update user role when it changes from AuthProvider or props
  useEffect(() => {
    if (userRoleProp) {
      setActualUserRole(userRoleProp);
    } else if (userRoleFromAuth) {
      setActualUserRole(userRoleFromAuth);
    }
  }, [userRoleProp, userRoleFromAuth]);

  // Rely on AuthProvider or API to fetch role
  // DO NOT make redundant API calls here - AuthProvider already handles this
  // If role is not available, it means AuthProvider is still loading or user is not authenticated
  // Just use the role from props (userRoleProp) which comes from AuthProvider via AppLayout

  if (!config) return null;

  // Filter items based on dashboard page
  let primaryNavItems = isDashboardPage ? [] : config.primaryNavItems;

  const isSellerAppPage =
    location.pathname.startsWith("/seller") &&
    !location.pathname.startsWith("/seller_detail");

  // Only hide these buttons on seller app pages or on specific content pages (pricing, search, product detail)
  if (isSellerAppPage || shouldHideNavButtons) {
    primaryNavItems = primaryNavItems.filter(
      (item) =>
        item.label !== "Categories" &&
        item.label !== "All products" &&
        item.label !== "Sellers" &&
        item.label !== "Pricing",
    );
  }

  // On chat page, also hide Products and RFQs for sellers
  if (isChatPage && (role === "seller" || actualUserRole === "seller")) {
    primaryNavItems = primaryNavItems.filter(
      (item) => item.label !== "Products" && item.label !== "RFQs",
    );
  }
  let userMenuItems: NavigationItem[];
  if (sellerUserMenuItems != null) {
    // Seller: same profile menu on every screen (driven by sidebar completion logic)
    userMenuItems = sellerUserMenuItems;
  } else {
    // Keep full menu on dashboard routes (buyer/admin); nav chrome is simplified elsewhere.
    userMenuItems = config.userMenuItems;
  }

  // Remove Messages from menu for admin users on home page
  const isHomePage = location.pathname === "/home" || location.pathname === "/";
  if (isHomePage && actualUserRole === "admin") {
    userMenuItems = userMenuItems.filter((item) => item.label !== "Messages");
  }
  const showRightButtons = !isDashboardPage && config.rightButtons;

  // Get user name and email - use real user data; avoid showing placeholder (e.g. "John Buyer") while loading
  const userName =
    emailPasswordUser?.name ??
    (hasToken && emailPasswordUser === null ? "Account" : config.userInfo.name);
  const userEmail =
    emailPasswordUser?.email ??
    (hasToken && emailPasswordUser === null ? "" : config.userInfo.subtitle);

  // Determine dashboard route based on actual user role (from props, AuthProvider, or API)
  const getDashboardRoute = async () => {
    // Priority 1: Use actualUserRole if available (from props or AuthProvider)
    if (actualUserRole) {
      if (actualUserRole === "admin") return "/admin/dashboard";
      if (actualUserRole === "seller") return "/seller/dashboard";
      if (actualUserRole === "buyer") return "/buyer/dashboard";
    }

    // Priority 2: Use role prop if available and not "both"
    if (role !== "both") {
      if (role === "admin") return "/admin/dashboard";
      if (role === "seller") return "/seller/dashboard";
      if (role === "buyer") return "/buyer/dashboard";
    }

    // Priority 3: For email/password users ONLY, fetch role from API
    if (hasToken) {
      try {
        const { authService } = await import("@b2b/services");
        const meResponse = await authService.getMe();

        if (meResponse.status === "active" && meResponse.user?.role) {
          const roleFromApi = meResponse.user.role.toLowerCase();

          if (
            roleFromApi === "super_admin" ||
            roleFromApi === "company_admin"
          ) {
            return "/admin/dashboard";
          } else if (
            roleFromApi === "seller" ||
            roleFromApi === "buyer_seller"
          ) {
            return "/seller/dashboard";
          } else if (roleFromApi === "buyer") {
            return "/buyer/dashboard";
          }
        }
      } catch (error) {
        console.error(
          "Error fetching user role for dashboard navigation:",
          error,
        );
      }
    }

    // Default fallback
    return "/buyer/dashboard";
  };

  // Determine profile route based on actual user role (from props, AuthProvider, or API)
  const getProfileRoute = async () => {
    // Priority 1: Use actualUserRole if available (from props or AuthProvider)
    if (actualUserRole) {
      if (actualUserRole === "admin") return "/admin/profile_settings";
      if (actualUserRole === "seller") return "/seller/profile";
      if (actualUserRole === "buyer") return "/buyer/profile";
    }

    // Priority 2: Use role prop if available and not "both"
    if (role !== "both") {
      if (role === "admin") return "/admin/profile_settings";
      if (role === "seller") return "/seller/profile";
      if (role === "buyer") return "/buyer/profile";
    }

    // Priority 3: For email/password users ONLY, fetch role from API
    if (hasToken) {
      try {
        const { authService } = await import("@b2b/services");
        const meResponse = await authService.getMe();

        if (meResponse.status === "active" && meResponse.user?.role) {
          const roleFromApi = meResponse.user.role.toLowerCase();

          if (
            roleFromApi === "super_admin" ||
            roleFromApi === "company_admin"
          ) {
            return "/admin/profile_settings";
          } else if (
            roleFromApi === "seller" ||
            roleFromApi === "buyer_seller"
          ) {
            return "/seller/profile";
          } else if (roleFromApi === "buyer") {
            return "/buyer/profile";
          }
        }
      } catch (error) {
        console.error(
          "Error fetching user role for profile navigation:",
          error,
        );
      }
    }

    // Default fallback
    return "/buyer/profile";
  };

  const handleLogout =
    onLogout ||
    (() => {
      try {
        localStorage.removeItem("bt_b2b_access_token");
        window.location.href = "/login";
      } catch {
        window.location.href = "/login";
      }
    });

  // Helpers
  const handleNavigate = (href: string) => {
    if (href && href !== "/logout") {
      // If navigating to dashboard, get the correct route based on role
      if (href.includes("dashboard") || href === "/") {
        getDashboardRoute().then((route) => {
          navigate(route);
        });
      } else {
        navigate(href);
      }
    }
  };
  const renderIcon = (name?: string) => {
    const Icon = name ? iconMap[name as keyof typeof iconMap] : null;
    return Icon ? <Icon className="h-4 w-4 mr-2 text-black" /> : null;
  };

  /** Match buyer profile dropdown: envelope icon for Messages (same as `icon: "Mail"`). */
  const renderUserMenuItemIcon = (item: NavigationItem) => {
    if (item.label === "Messages") {
      return <Mail className="h-4 w-4 mr-2 text-black" />;
    }
    return renderIcon(item.icon);
  };

  // Render nav (top links)
  const NavItem = ({ item }: { item: NavigationItem }) =>
    item.children?.length ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="hover:bg-orange-100 text-black">
            {renderIcon(item.icon)}
            {item.label}
            <ChevronDown className="h-4 w-4 ml-1 text-black" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="p-0">
          {item.children.map((c) => (
            <DropdownMenuItem
              key={c.label}
              onClick={() => handleNavigate(c.href || "")}
              className="text-black focus:bg-orange-100 bg-white"
            >
              {renderIcon(c.icon)} {c.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <Button
        key={item.label}
        variant="ghost"
        className="hover:bg-orange-100 text-black"
        onClick={() => handleNavigate(item.href || "")}
      >
        {renderIcon(item.icon)} {item.label}
      </Button>
    );

  // Render user menu
  const UserMenuItem = ({ item }: { item: NavigationItem }) => {
    if (item.label === "Logout") {
      return (
        <DropdownMenuItem
          key={item.label}
          onClick={handleLogout}
          className="text-black focus:bg-orange-100"
        >
          <LogOut className="h-4 w-4 mr-2 text-black" /> {item.label}
        </DropdownMenuItem>
      );
    }
    // Handle Profile / Business Profile navigation
    if (item.label === "Profile" || item.label === "Business Profile") {
      return (
        <DropdownMenuItem
          key={item.label}
          onClick={() => {
            const href = item.href;
            if (href) {
              navigate(href);
            } else {
              getProfileRoute().then((route) => navigate(route));
            }
          }}
          className="text-black focus:bg-orange-100"
        >
          {renderUserMenuItemIcon(item)} {item.label}
        </DropdownMenuItem>
      );
    }
    return (
      <DropdownMenuItem
        key={item.label}
        onClick={() => handleNavigate(item.href || "")}
        className="text-black focus:bg-orange-100"
      >
        {renderUserMenuItemIcon(item)} {item.label}
      </DropdownMenuItem>
    );
  };

  const leftCluster = (
    <div className="flex items-center gap-6 min-w-0">
      <Button
        variant="ghost"
        size="sm"
        onClick={onToggleSidebar}
        className="lg:hidden hover:bg-orange-100 text-black"
      >
        {isSidebarOpen ? <IoMdClose size={24} /> : <FiMenu size={24} />}
      </Button>

      <BizcoverIndiaBrandMark variant="header" to="/home" />

      <nav className="hidden lg:flex items-center gap-1">
        {primaryNavItems.map((i) => (
          <NavItem key={i.label} item={i} />
        ))}
      </nav>
    </div>
  );

  const headerActions = (
    <div className="flex items-center gap-2 sm:gap-3 shrink-0">
      {showRightButtons &&
        typeof showRightButtons === "object" &&
        showRightButtons.cart && (
          <Button
            variant="ghost"
            size="sm"
            className="relative hover:bg-orange-100 text-black"
          >
            <ShoppingCart className="h-4 w-4 mr-1" />{" "}
            <span className="hidden lg:inline">Cart</span>
            {config.badges?.cart && (
              <Badge className="absolute -top-2 -right-2 h-5 w-5 p-1 text-xs bg-red-600 text-white">
                {config.badges.cart}
              </Badge>
            )}
          </Button>
        )}

      {/* Messages: show on hub/simplified header too; not gated by showRightButtons (cart/notifications). Hidden on /chat. */}
      {!isChatPage &&
        actualUserRole !== "admin" &&
        Boolean(config.rightButtons?.messages) && (
          <HeaderChatMessagesSlot
            chatUnread={chatUnread}
            showMessagesButton
            isAuthenticated={isAuthenticated}
            variant="compact"
          />
        )}
      {isAuthenticated && Boolean(config.rightButtons?.notifications) ? (
          <HeaderNotificationsSlot
            notifications={notifications}
            showNotificationsButton
            isAuthenticated={isAuthenticated}
            variant="compact"
          />
        ) : null}

      {!isAuthenticated && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-8 rounded-full border-gray-300 text-gray-700 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700"
            onClick={() => navigate("/registration")}
          >
            Create Account
          </Button>
          <Button
            className="h-8 rounded-full bg-orange-500 hover:bg-orange-600 text-white"
            onClick={() => navigate("/login")}
          >
            Sign In
          </Button>
        </div>
      )}

      {isAuthenticated && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center hover:bg-orange-100 text-black outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              <Avatar className="h-7 w-7 mr-2">
                <AvatarImage
                  src={buyerAvatarUrl || "/placeholder.svg"}
                />
                <AvatarFallback className="bg-gray-200 text-black text-xs">
                  {userName?.charAt(0)?.toUpperCase() || (
                    <User className="h-3 w-3" />
                  )}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:inline">
                {userName ||
                  (role === "admin"
                    ? "Admin"
                    : role === "buyer"
                      ? "Account"
                      : "Seller")}
              </span>
              <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-white">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <p className="text-sm font-medium text-black">{userName}</p>
                <p className="text-xs text-gray-500">{userEmail}</p>
              </div>
            </DropdownMenuLabel>
            {/* <DropdownMenuSeparator /> */}
            {isAuthenticated && (
              <>
                <DropdownMenuItem
                  onClick={() => {
                    getDashboardRoute().then((route) => {
                      navigate(route);
                    });
                  }}
                  className="text-black focus:bg-orange-100"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2 text-black" /> My
                  Dashboard
                </DropdownMenuItem>
                {/* <DropdownMenuSeparator /> */}
              </>
            )}
            {userMenuItems.map((item) =>
              item.label === "Logout" ? (
                <React.Fragment key={item.label}>
                  {/* <DropdownMenuSeparator /> */}
                  <UserMenuItem item={item} />
                </React.Fragment>
              ) : item.label === "My Dashboard" ? null : (
                <UserMenuItem key={item.label} item={item} />
              ),
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  return (
    <div className="sticky top-0 z-40 shrink-0 w-full bg-white border-b border-gray-200 shadow-sm">
      <header
        className="h-16 px-4 sm:px-6 flex items-center justify-between"
      >
        {!headerCenter ? (
          <>
            {leftCluster}
            {searchBar && (
              <div className="w-full min-w-0 lg:flex-1 lg:max-w-3xl">
                {searchBar}
              </div>
            )}
            {headerActions}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between w-full gap-3 lg:w-auto lg:shrink-0 lg:justify-start">
              {leftCluster}
              <div className="lg:hidden">{headerActions}</div>
            </div>
            <div className="w-full min-w-0 lg:flex-1 lg:max-w-3xl">
              {headerCenter}
            </div>
            <div className="hidden lg:flex shrink-0">{headerActions}</div>
          </>
        )}
      </header>
    </div>
  );
};

export { Header };
